import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Spinner } from '../../components/ui/Spinner.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { salesApi } from '../../services/salesApi.js';
import { formatDate, formatNumber } from '../../utils/formatters.js';
import { downloadDeliveryPdf, previewDeliveryPdf } from '../../utils/pdf.js';

import { DeliverySendModal } from './DeliverySendModal.jsx';

/* Kein Rückfall auf einen fremden Absender.
 *
 * Hier standen fest verdrahtet Recicas Name und Recicas E-Mail. Solange es
 * eine Installation gab, war das ein Platzhalter. Mit mehreren Kundenfirmen
 * wird daraus ein Fehler mit Aussenwirkung: eine Firma, die ihre Angaben noch
 * nicht ausgefüllt hat, verschickt ihre Rechnung mit dem Briefkopf und der
 * Kontaktadresse eines fremden Unternehmens.
 *
 * Leer ist die ehrlichere Antwort. Wer den eigenen Namen auf der Rechnung
 * vermisst, trägt ihn in den Einstellungen nach. */

export function DeliveryPrintPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [sendOpen, setSendOpen] = useState(false);

  const delivery = useQuery({
    queryKey: ['sales', 'deliveries', id],
    queryFn: () => salesApi.getDelivery(id).then((r) => r.items),
  });
  const settings = useQuery({
    queryKey: ['finance', 'company-settings'],
    queryFn: () => financeApi.getCompanySettings().then((r) => r.items),
  });
  const d = delivery.data;
  const customer = useQuery({
    queryKey: ['sales', 'customer', d?.customerId],
    queryFn: () => salesApi.getCustomer(d.customerId).then((r) => r.items),
    enabled: Boolean(d?.customerId),
  });

  if (delivery.isLoading) {
    return <section className="page"><Spinner /></section>;
  }
  if (delivery.error || !d) {
    return (
      <section className="page">
        <div className="alert danger">{delivery.error?.message || 'Not found'}</div>
        <Link to="/sales/deliveries" className="btn">← {t('nav.deliveries')}</Link>
      </section>
    );
  }

  const cfg = settings.data || {};
  const party = customer.data;

  return (
    <div className="invoice-print-wrap">
      <div className="invoice-toolbar no-print">
        <Link to="/sales/deliveries" className="btn">← {t('common.back')}</Link>
        <div className="invoice-toolbar-actions">
          <button type="button" className="btn" onClick={() => previewDeliveryPdf({ delivery: d, customer: party, settings: settings.data, t })}>
            {t('pdf.preview')}
          </button>
          <button type="button" className="btn"
            onClick={() => downloadDeliveryPdf({ delivery: d, customer: party, settings: settings.data, t })}>
            {t('invoice.print.downloadPdf')}
          </button>
          <button type="button" className="btn" onClick={() => setSendOpen(true)}>
            {t('email.send')}
          </button>
          <button type="button" className="btn primary" onClick={() => window.print()}>
            {t('invoice.print.printOrSave')}
          </button>
        </div>
      </div>

      <article className="invoice-doc">
        <header className="invoice-doc-head">
          <div className="invoice-doc-brand">
            <div className="invoice-doc-brand-name">{cfg.companyName}</div>
            {cfg.addressLine1 && <div className="invoice-doc-muted">{cfg.addressLine1}</div>}
            {(cfg.zip || cfg.city) && <div className="invoice-doc-muted">{[cfg.zip, cfg.city].filter(Boolean).join(' ')}</div>}
            <div className="invoice-doc-muted">{cfg.email}</div>
          </div>
          <div className="invoice-doc-title">
            <h1>{t('delivery.print.heading')}</h1>
            <div className="invoice-doc-number">{d.deliveryNumber}</div>
          </div>
        </header>

        <section className="invoice-doc-parties">
          <div>
            <div className="invoice-doc-label">{t('delivery.print.shipTo')}</div>
            <div className="invoice-doc-party-name">{party?.name || '-'}</div>
            {d.shipToAddress && <div className="invoice-doc-muted">{d.shipToAddress}</div>}
          </div>
          <div className="invoice-doc-meta">
            <div><span>{t('delivery.deliveryDate')}</span><strong>{formatDate(d.deliveryDate)}</strong></div>
            {d.shippingDate && <div><span>{t('delivery.shippingDate')}</span><strong>{formatDate(d.shippingDate)}</strong></div>}
            {d.carrier && <div><span>{t('delivery.carrier')}</span><strong>{d.carrier}</strong></div>}
            {d.trackingNumber && <div><span>{t('delivery.tracking')}</span><strong>{d.trackingNumber}</strong></div>}
            {d.packageCount != null && <div><span>{t('delivery.packages')}</span><strong>{d.packageCount}</strong></div>}
            {d.totalWeight != null && <div><span>{t('delivery.weight')}</span><strong>{d.totalWeight} kg</strong></div>}
          </div>
        </section>

        <table className="invoice-doc-table">
          <thead>
            <tr>
              <th>{t('detail.lineDescription')}</th>
              <th className="num">{t('delivery.qtyDelivered')}</th>
            </tr>
          </thead>
          <tbody>
            {(d.lines || []).map((r) => (
              <tr key={r.id}>
                <td>{r.description}</td>
                <td className="num">{formatNumber(r.quantity)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {d.notes && (
          <section className="invoice-doc-notes">
            <div className="invoice-doc-label">{t('detail.notes')}</div>
            <p>{d.notes}</p>
          </section>
        )}

        <footer className="invoice-doc-footer">{[cfg.companyName, cfg.email].filter(Boolean).join(' · ')}</footer>
      </article>
      <DeliverySendModal delivery={sendOpen ? d : null} onClose={() => setSendOpen(false)} />
    </div>
  );
}
